import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, isSameMonth, parseISO,
} from "date-fns";
import { Plane, ChevronLeft, ChevronRight, Clock, ArrowRight, ArrowLeft, Loader2, CreditCard } from "lucide-react";
import { api, formatSlotTime, formatAud, formatApiErrorDetail } from "@/lib/api";
import { useSeo } from "@/hooks/use-seo";
import { Navbar, Footer } from "@/pages/Home";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Book() {
  useSeo({
    title: "Book a Cabin Crew Mentoring Session | Cabin Crew Mentoring",
    description: "Pick your session, choose a time that suits you, and get interview ready with Tony — former flight attendant and airline hiring team member.",
    path: "/book",
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [sessions, setSessions] = useState([]);
  const [slots, setSlots] = useState([]);
  const [sessionKey, setSessionKey] = useState(searchParams.get("session") || "");
  const [slotId, setSlotId] = useState("");
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/slots").then((r) => setSlots(r.data)).catch(() => {});
  }, []);

  const slotsByDate = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      map[s.date] = map[s.date] || [];
      map[s.date].push(s);
    });
    return map;
  }, [slots]);

  const selectedSession = sessions.find((s) => s.key === sessionKey);
  const selectedSlot = slots.find((s) => s.id === slotId);

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    const days = [];
    let d = start;
    while (d <= end) {
      days.push(d);
      d = addDays(d, 1);
    }
    return days;
  }, [month]);

  const selectedDateStr = selectedSlot?.date || null;

  const handlePay = async () => {
    setError("");
    setLoading(true);
    try {
      const bookingRes = await api.post("/bookings", {
        session_key: sessionKey,
        slot_id: slotId,
        name: form.name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
      });
      const checkoutRes = await api.post("/payments/checkout", {
        booking_id: bookingRes.data.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkoutRes.data.checkout_url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const canNext =
    (step === 1 && sessionKey) ||
    (step === 2 && slotId) ||
    (step === 3 && form.name.trim() && /\S+@\S+\.\S+/.test(form.email));

  const stepTitles = ["Choose your session", "Pick a date & time", "Your details", "Review & pay"];

  return (
    <div data-testid="book-page" className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-28 pb-24 max-w-4xl mx-auto px-4 sm:px-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-sky-700">
          <Plane className="w-4 h-4" /> Booking — Step {step} of 4
        </div>
        <h1 data-testid="book-step-title" className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A192F] mt-2">
          {stepTitles[step - 1]}
        </h1>

        <div className="mt-4 flex gap-2">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} data-testid={`step-indicator-${n}`} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${n <= step ? "bg-sky-600" : "bg-slate-200"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.3 }}
            className="mt-10"
          >
            {step === 1 && (
              <div className="grid sm:grid-cols-3 gap-4">
                {sessions.map((s) => (
                  <button
                    key={s.key}
                    data-testid={`select-session-${s.key}`}
                    onClick={() => setSessionKey(s.key)}
                    className={`text-left rounded-xl border-2 p-6 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 ${
                      sessionKey === s.key ? "border-sky-600 bg-white shadow-lg" : "border-slate-200 bg-white hover:border-sky-300"
                    }`}
                  >
                    <div className="font-display text-2xl font-bold text-[#F59E0B]">{formatAud(s.price_cents)}</div>
                    <div className="font-semibold text-[#0A192F] mt-1">{s.name}</div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> {s.duration}</div>
                    <p className="text-xs text-slate-500 mt-3 leading-relaxed">{s.tagline}</p>
                  </button>
                ))}
              </div>
            )}

            {step === 2 && (
              <div className="grid md:grid-cols-[1.2fr_1fr] gap-6">
                <div className="rounded-xl bg-white border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <button data-testid="cal-prev-month" onClick={() => setMonth(addMonths(month, -1))} className="p-2 rounded-full hover:bg-slate-100 transition-colors duration-150">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div data-testid="cal-month-label" className="font-display text-xl font-bold uppercase tracking-wide text-[#0A192F]">
                      {format(month, "MMMM yyyy")}
                    </div>
                    <button data-testid="cal-next-month" onClick={() => setMonth(addMonths(month, 1))} className="p-2 rounded-full hover:bg-slate-100 transition-colors duration-150">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    {WEEKDAYS.map((d) => <div key={d}>{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {calendarDays.map((day) => {
                      const key = format(day, "yyyy-MM-dd");
                      const hasSlots = !!slotsByDate[key]?.length;
                      const isSelected = selectedDateStr === key;
                      return (
                        <button
                          key={key}
                          data-testid={`cal-day-${key}`}
                          disabled={!hasSlots}
                          onClick={() => setSlotId(slotsByDate[key][0].id)}
                          className={`aspect-square rounded-lg text-sm font-medium transition-colors duration-150 ${
                            !isSameMonth(day, month)
                              ? "text-slate-300"
                              : hasSlots
                                ? isSelected
                                  ? "bg-[#0A192F] text-white"
                                  : "bg-sky-50 text-sky-800 hover:bg-sky-100"
                                : "text-slate-300 cursor-not-allowed"
                          }`}
                        >
                          {format(day, "d")}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-400 mt-4">All times are Sydney time (AEST/AEDT). Days with availability are highlighted.</p>
                </div>
                <div className="rounded-xl bg-white border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#0A192F]">
                    {selectedDateStr ? format(parseISO(selectedDateStr), "EEEE, d MMMM") : "Select a date first"}
                  </h3>
                  <div data-testid="time-slots-list" className="mt-4 space-y-2">
                    {selectedDateStr && slotsByDate[selectedDateStr]?.map((s) => (
                      <button
                        key={s.id}
                        data-testid={`slot-btn-${s.id}`}
                        onClick={() => setSlotId(s.id)}
                        className={`w-full rounded-lg border-2 px-4 py-3 text-sm font-semibold flex items-center justify-between transition-colors duration-150 ${
                          slotId === s.id ? "border-sky-600 bg-sky-50 text-sky-800" : "border-slate-200 hover:border-sky-300 text-slate-700"
                        }`}
                      >
                        <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> {formatSlotTime(s.time)}</span>
                        {slotId === s.id && <span className="text-xs uppercase tracking-widest">Selected</span>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="rounded-xl bg-white border border-slate-200 p-8 grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="bk-name">Full name *</Label>
                  <Input id="bk-name" data-testid="booking-name-input" className="mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Citizen" />
                </div>
                <div>
                  <Label htmlFor="bk-email">Email *</Label>
                  <Input id="bk-email" data-testid="booking-email-input" type="email" className="mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" />
                </div>
                <div>
                  <Label htmlFor="bk-phone">Phone (optional)</Label>
                  <Input id="bk-phone" data-testid="booking-phone-input" className="mt-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="04xx xxx xxx" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="bk-notes">Anything Tony should know? (airline applying to, where you're at)</Label>
                  <Textarea id="bk-notes" data-testid="booking-notes-input" className="mt-2" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Applying to Qantas, assessment day in 3 weeks..." />
                </div>
              </div>
            )}

            {step === 4 && selectedSession && selectedSlot && (
              <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
                <div className="navy-panel text-white px-8 py-5 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-sky-400 font-semibold">Your flight plan</div>
                    <div data-testid="review-session-name" className="font-display text-2xl font-bold uppercase mt-1">{selectedSession.name}</div>
                  </div>
                  <Plane className="w-8 h-8 text-[#F59E0B]" />
                </div>
                <div className="p-8 grid sm:grid-cols-2 gap-6 text-sm">
                  <div><div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Passenger</div><div data-testid="review-name" className="font-semibold mt-1">{form.name}</div><div className="text-slate-500">{form.email}</div></div>
                  <div><div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Departure</div><div data-testid="review-datetime" className="font-semibold mt-1">{format(parseISO(selectedSlot.date), "EEEE, d MMMM yyyy")} — {formatSlotTime(selectedSlot.time)}</div><div className="text-slate-500">Sydney time, via video call</div></div>
                  <div><div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Duration</div><div className="font-semibold mt-1">{selectedSession.duration}</div></div>
                  <div><div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Total</div><div data-testid="review-price" className="font-display text-3xl font-bold text-[#0A192F] mt-1">{formatAud(selectedSession.price_cents)}</div></div>
                </div>
                {error && <div data-testid="booking-error" className="mx-8 mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{error}</div>}
                <div className="px-8 pb-8">
                  <Button
                    data-testid="pay-now-btn"
                    onClick={handlePay}
                    disabled={loading}
                    className="w-full rounded-full bg-[#F59E0B] hover:bg-amber-400 text-[#0A192F] font-bold py-6 text-base"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="flex items-center gap-2"><CreditCard className="w-5 h-5" /> Pay {formatAud(selectedSession.price_cents)} Securely with Stripe</span>}
                  </Button>
                  <p className="text-xs text-slate-400 text-center mt-3">You'll be redirected to Stripe's secure checkout. Your time slot is held while you pay.</p>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between">
          {step > 1 ? (
            <Button data-testid="wizard-back-btn" variant="outline" onClick={() => setStep(step - 1)} className="rounded-full px-6">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          ) : <span />}
          {step < 4 && (
            <Button
              data-testid="wizard-next-btn"
              onClick={() => canNext && setStep(step + 1)}
              disabled={!canNext}
              className="rounded-full bg-[#0A192F] hover:bg-sky-800 px-8"
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
